/**
 * Error telemetry, lazy and optional. Sentry is loaded ONLY when
 * `config.sentryDsn` is set, and only after first paint via requestIdleCallback,
 * so its ~30 KB lands in its own async chunk and never touches the initial-JS
 * budget. With no DSN the whole thing is inert. Errors captured
 * before Sentry finishes loading are buffered and flushed on init.
 */
import type { AppConfig } from "./config.js";

type Captured = { error: unknown; context?: Record<string, unknown> };

let sink: ((c: Captured) => void) | null = null;
const buffer: Captured[] = [];
const MAX_BUFFER = 20;

/** Report an error to telemetry. No-op (buffered) until Sentry loads; inert forever without a DSN. */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  const entry = { error, context };
  if (sink) {
    sink(entry);
    return;
  }
  if (buffer.length < MAX_BUFFER) buffer.push(entry);
}

const idle = (cb: () => void): void => {
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
  if (ric) ric(cb);
  else setTimeout(cb, 1500);
};

/**
 * Wire telemetry from runtime config. Safe to call once at boot; does nothing
 * unless a DSN is configured. Loads Sentry off the critical path.
 */
export function initTelemetry(config: AppConfig): void {
  if (!config.sentryDsn || sink) return;
  idle(() => {
    void import("@sentry/react")
      .then((Sentry) => {
        Sentry.init({
          dsn: config.sentryDsn,
          environment: config.environment,
          release: config.release,
          // We never want PII from URLs/inputs; keep it lean.
          tracesSampleRate: 0,
        });
        sink = ({ error, context }) => Sentry.captureException(error, context ? { extra: context } : undefined);
        for (const entry of buffer.splice(0)) sink(entry);
      })
      .catch(() => {
        // Telemetry must never break the app — if Sentry fails to load, stay inert.
      });
  });
}
